import React, {createContext, useContext, ReactNode, useEffect, useState} from 'react';
//import axios from "axios";
//import { generateImageOnRunPod } from "./runpodService";
import {DashboardGenParams, WORKFLOW, loadWorkflow} from "./api";

type JobStatus = "IDLE" | "IN_PROGRESS" | "FAILED" | "COMPLETED" | "CANCELLED" | "TIMED_OUT" | "IN_QUEUE";

interface DataContextProps {
    fetchCheckpoints: () => Promise<string[][]>;
    queuePrompt: (params: DashboardGenParams) => Promise<any>;

    outputImage: string | null;
    queueStatus: number;
    jobStatus: JobStatus;

    setOutputImage: (img: string | null) => void;
    setQueueStatus: (val: number) => void;
    setJobStatus: (status: JobStatus) => void;

}

const DataContext = createContext<DataContextProps | undefined>(undefined);

interface DataProviderProps {
    children: ReactNode;
}


export const ComfyProvider: React.FC<DataProviderProps> = ({ children }) => {




    const [outputImage, setOutputImage] = useState<string | null>(null);
    const [queueStatus, setQueueStatus] = useState(0);
    const [jobStatus, setJobStatus] = useState<JobStatus>("IDLE");

    const GetWebSocket = async (runpodResponse: any) => {
        if (!runpodResponse || !runpodResponse.id) {
            console.error("Invalid RunPod response, cannot establish WebSocket-like behavior.");
            return;
        }

        const runpodJobId = runpodResponse.id;
        console.log("Tracking RunPod job ID:", runpodJobId);

        const pollRunPodStatus = async () => {
            try {
                const statusUrl = `https://api.runpod.ai/v2/${process.env.REACT_APP_RUNPOD_SERVERLESS_ID}/status/${runpodJobId}`;
                const response = await fetch(statusUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${process.env.REACT_APP_RUNPOD_API_KEY}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                //console.log("RunPod Job Status:", data);

                switch (data.status) {
                    case "COMPLETED":
                        if (data.output?.status === "success" && data.output?.message) {
                            setOutputImage(`data:image/png;base64,${data.output.message}`);
                            setJobStatus("COMPLETED");
                        } else if (data.output?.status === "error") {
                            setJobStatus("FAILED");
                        }
                        break;

                    case "IN_PROGRESS":
                        setJobStatus("IN_PROGRESS");
                        break;

                    case "IN_QUEUE":
                        setQueueStatus(prev => prev + 1);
                        setJobStatus("IN_QUEUE");
                        break;

                    case "FAILED":
                        setJobStatus("FAILED");
                        break;

                    case "CANCELLED":
                        setJobStatus("CANCELLED");
                        break;

                    case "TIMED_OUT":
                        setJobStatus("TIMED_OUT");
                        break;

                    default:
                        console.warn("Unknown status:", data.status);
                        break;
                }

                if (!["COMPLETED", "FAILED", "CANCELLED", "TIMED_OUT"].includes(data.status)) {
                    setTimeout(pollRunPodStatus, 3000); // Poll again after 3 seconds
                }
            } catch (error) {
                console.error("Error polling RunPod job status:", error);
            }
        };

        pollRunPodStatus();
    };


    useEffect(() => {
        loadWorkflow(); 
    }, []);

    const extractBase64 = (dataUrl: string) => {
        return dataUrl.split(",")[1] || dataUrl; // Extract base64 data, fallback to original if no comma
    };


    const queuePrompt = async (params: DashboardGenParams) => {

        await loadWorkflow();
        console.log(params);



        /*Input Image */
        WORKFLOW["2049"].inputs.base64_image = extractBase64(params.inImage);

        //CNET
        WORKFLOW[1787].inputs.strength = params.cnetVolume //volume
        WORKFLOW[1763].inputs.strenght = params.cnetEdges //edges
        
        let condIndex = 1; // Starts from 1
        let checkedColorsCount = 0; // Track how many colors are checked
        
        const panel = "2486"; 
        const blank = "2487"; 
        
        const colors = [
            { key: "cyan", nodes: { detector: "2164", mask1: "2204", mask2: "2202", mask3: "2203", condpos: "2277", crop: "2308", ref: "2328", prompt: "2310", negPrompt: "2311", ipadap: "2309" } },
            { key: "blue", nodes: { detector: "2165", mask1: "2146", mask2: "2143", mask3: "2145", condpos: "2276", crop: "2301", ref: "2330", prompt: "2303", negPrompt: "2304", ipadap: "2302" } },
            { key: "lime", nodes: { detector: "2166", mask1: "2242", mask2: "2243", mask3: "2244", condpos: "1614", crop: "1623", ref: "2331", prompt: "2296", negPrompt: "1744", ipadap: "1615" } },
            { key: "green", nodes: { detector: "2167", mask1: "2183", mask2: "2180", mask3: "2182", condpos: "2275", crop: "2283", ref: "2332", prompt: "1861", negPrompt: "2281", ipadap: "2279" } },
            { key: "red", nodes: { detector: "2168", mask1: "2115", mask2: "2114", mask3: "2111", condpos: "1616", crop: "1624", ref: "2464", prompt: "1871", negPrompt: "1877", ipadap: "1617" } },
            { key: "vegetation", nodes: { detector: "2169", mask1: "2267", mask2: "2246", mask3: "2266", condpos: "2278", crop: "2285", ref: "2465", prompt: "2291", negPrompt: "2290", ipadap: "2288" } }
        ] as const;
        
        type ColorKey = "cyan" | "blue" | "lime" | "green" | "red" | "vegetation";
        
        // First pass: Count how many colors are checked
        for (const { key } of colors) {
            if (params.checkedColors[key as ColorKey]) {
                checkedColorsCount++;
            }
        }
        
        // Process colors
        colors.forEach(({ key, nodes }) => {
            if (params.checkedColors[key as ColorKey]) {  
                // If color is checked, send normal values
                if (params[`${key}Reference`]) {
                    WORKFLOW[nodes.ref].inputs.base64_image = extractBase64(params[`${key}Reference`]);
                    WORKFLOW[nodes.ipadap].inputs.image_weight = params[`${key}ImgWeight`];
                } else {
                    delete WORKFLOW[nodes.ref]; // Remove reference input
                    delete WORKFLOW[nodes.crop]; // Remove crop image
                    WORKFLOW[nodes.ipadap].inputs.image = ["2049", 0]; // Placeholder
                    WORKFLOW[nodes.ipadap].inputs.image_weight = 0.001;
                }
        
                WORKFLOW[nodes.prompt].inputs.string = params[`${key}Prompt`] || "";
                WORKFLOW[nodes.negPrompt].inputs.string = params[`${key}Negative`] || "";
                WORKFLOW[nodes.ipadap].inputs.prompt_weight = params[`${key}PromptWeight`] || 1.0;
        
                // Assign conditionings dynamically
                WORKFLOW["1964"].inputs[`conditioning${condIndex}`] = [nodes.condpos, 0];
                WORKFLOW["2298"].inputs[`conditioning${condIndex}`] = [nodes.ipadap, 1];
                WORKFLOW["1968"].inputs[`conditioning${condIndex}`] = [nodes.ipadap, 2];
        
                condIndex++; // Increment only for checked colors
        
            } else {  
                // If color is NOT checked, modify necessary values using panel and blank variables
                WORKFLOW[nodes.mask1].inputs.images = [panel, 0];
                WORKFLOW[nodes.mask2].inputs.images = [panel, 0];
                WORKFLOW[nodes.mask3].inputs.images = [panel, 0];
        
                WORKFLOW[nodes.detector].inputs.image = [panel, 0];
                WORKFLOW[nodes.detector].inputs.target_color = [blank, 0];
            }
        });
        
        // Add the fallback conditioning
        if (checkedColorsCount === 0) {
            // No colors were checked, use default conditioning at index 1
            WORKFLOW["1964"].inputs[`conditioning1`] = ["1957", 0];
            WORKFLOW["2298"].inputs[`conditioning1`] = ["2341", 0];
            WORKFLOW["1968"].inputs[`conditioning1`] = ["1966", 0];
        } else {
            // We have some checked colors, add the fallback as the last conditioning
            WORKFLOW["1964"].inputs[`conditioning${condIndex}`] = ["1957", 0];
            WORKFLOW["2298"].inputs[`conditioning${condIndex}`] = ["2341", 0];
            WORKFLOW["1968"].inputs[`conditioning${condIndex}`] = ["1966", 0];
        }
        
        // IMPORTANT: Clean up any unused conditioning inputs
        // This prevents the error when there are fewer conditionings than before
        const maxConditionings = colors.length + 1; // Max possible number of conditionings
        
        // Clear any unused conditioning inputs from previous runs
        for (let i = condIndex + 1; i <= maxConditionings; i++) {
            delete WORKFLOW["1964"].inputs[`conditioning${i}`];
            delete WORKFLOW["2298"].inputs[`conditioning${i}`];
            delete WORKFLOW["1968"].inputs[`conditioning${i}`];
        }

        /*General Prompt */
        WORKFLOW["1860"].inputs.string = params.positivePrompt;
        
        WORKFLOW["2469"].inputs.lora_name = params.stylePrompt.trim();
        if (params.style === "sketch" || params.style === "picto")  {
            delete WORKFLOW["2468"];
            WORKFLOW["1609"].inputs.model = ["2469", 0];
            WORKFLOW["1742"].inputs.clip = ["2469", 1];
            WORKFLOW["1882"].inputs.clip = ["2469", 1];
            WORKFLOW["1958"].inputs.clip = ["2469", 1];
            WORKFLOW["1965"].inputs.clip = ["2469", 1];
            WORKFLOW["1992"].inputs.clip = ["2469", 1];
            WORKFLOW["1994"].inputs.clip = ["2469", 1];
            WORKFLOW["2282"].inputs.clip = ["2469", 1];
            WORKFLOW["2289"].inputs.clip = ["2469", 1];
            WORKFLOW["2292"].inputs.clip = ["2469", 1];
            WORKFLOW["2294"].inputs.clip = ["2469", 1];
            WORKFLOW["2300"].inputs.clip = ["2469", 1];
            WORKFLOW["2305"].inputs.clip = ["2469", 1];
            WORKFLOW["2307"].inputs.clip = ["2469", 1];
            WORKFLOW["2312"].inputs.clip = ["2469", 1];
            WORKFLOW["2348"].inputs.clip = ["2469", 1];
        };
    
        WORKFLOW["2030"].inputs.cfg = params.cfg;
        WORKFLOW["2030"].inputs.steps = params.steps;
        WORKFLOW["1795"].inputs.seed = params.seed;

        /*Upscaler Logic*/
        if (params.upscaler === false) { //deletes all nodes related to the final upscaler
            delete WORKFLOW["2429"];
            delete WORKFLOW["2425"];
            delete WORKFLOW["2434"];
            delete WORKFLOW["2431"];
            delete WORKFLOW["2456"];
            delete WORKFLOW["2445"];
            delete WORKFLOW["2444"];
            delete WORKFLOW["2427"];
            delete WORKFLOW["2426"];
            delete WORKFLOW["2440"];
            delete WORKFLOW["2436"];
            delete WORKFLOW["2415"];
            delete WORKFLOW["2441"];
            delete WORKFLOW["2432"];
            delete WORKFLOW["2414"];
            delete WORKFLOW["2435"];
            delete WORKFLOW["2433"];
            delete WORKFLOW["2438"];
            delete WORKFLOW["2413"];
            delete WORKFLOW["2442"];
            delete WORKFLOW["2430"];
            delete WORKFLOW["2448"]; //Image HD           
        }else{
            delete WORKFLOW["2377"] //Image SD
            if (params.style === "sketch"){
                delete WORKFLOW["2425"]; //Real
                delete WORKFLOW["2434"]; //Real
                WORKFLOW["2431"].inputs.model = ["2429", 0];
                WORKFLOW["2431"].inputs.clip = ["2429", 1];
                
                delete WORKFLOW["2456"]; //Picto
                WORKFLOW["2415"].inputs.model = ["2431", 0];
                WORKFLOW["2444"].inputs.clip = ["2431", 1];
                WORKFLOW["2445"].inputs.clip = ["2431", 1];

            }else if (params.style === "picto"){
                delete WORKFLOW["2425"]; //Real
                delete WORKFLOW["2434"]; //Real
                delete WORKFLOW["2431"] //Sketch
                WORKFLOW["2456"].inputs.model = ["2429", 0];
                WORKFLOW["2456"].inputs.clip = ["2429", 1];

            }else{
                delete WORKFLOW["2431"] //Sketch
                delete WORKFLOW["2456"] //Picto
                WORKFLOW["2415"].inputs.model = ["2434", 0];
                WORKFLOW["2444"].inputs.clip = ["2434", 1];
                WORKFLOW["2445"].inputs.clip = ["2434", 1];
            }
        };

        const data = { workflow: WORKFLOW, client_id: "1122" };
        //console.log("Workflow sent: ", WORKFLOW); 
        console.log("Workflow sent!"); 

 //custom node comfy api
        /*
        try {
            const response = await generateImageOnRunPod(WORKFLOW);
            console.log("RunPod Response:", response);
            return response;
        } catch (error) {
            console.error("Error in queuePrompt:", error);
            throw error;
        }
    }; */
        
        //custom node comfy api
        const url = `https://api.runpod.ai/v2/${process.env.REACT_APP_RUNPOD_SERVERLESS_ID}/run`;
        const requestConfig = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${process.env.REACT_APP_RUNPOD_API_KEY}`
            },
            body: JSON.stringify({input: data})
        };

        try {
            const response = await fetch(url, requestConfig);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    
            const runpodResponse = await response.json();
            //console.log("RunPod Response:", runpodResponse);
            
            await GetWebSocket(runpodResponse);
            return runpodResponse;
            
        } catch (error) {
            console.error("Error in queuePrompt:", error);
            throw error;
        }
        
    };

    

    return (
        <DataContext.Provider value={{ 
            fetchCheckpoints: async () => [], 
            queuePrompt,
            outputImage,
            queueStatus,
            jobStatus,
            setOutputImage,
            setQueueStatus,
            setJobStatus,
            }}>
            {children}
        </DataContext.Provider>
        
    );
};

export const useComfy = (): DataContextProps => {
    const context = useContext(DataContext);
    if (!context) {
        throw new Error('useComfy must be used within a ComfyProvider');
    }
    return context;
};